import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, NotebookPen, CalendarDays } from "lucide-react";

const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const months = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
  const idx = Math.max(0, Math.min(11, parseInt(m, 10) - 1));
  return `${months[idx]} ${y}`;
};

export const JournalSection = ({ clan, canEdit }) => {
  const { API, user } = useAuth();
  const { t } = useLang();
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ month: new Date().toISOString().slice(0, 7), content: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clans/${clan.key}/journal`);
      setEntries(data);
    } catch (err) {
      console.error("Journal load failed:", err);
    }
  }, [API, clan.key]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.content.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/clans/${clan.key}/journal`, form);
      toast.success(t("journalSaved"));
      setOpen(false);
      setForm({ month: new Date().toISOString().slice(0, 7), content: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/clans/${clan.key}/journal/${id}`);
      toast.success(t("journalDeleted"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <section className="heritage-card p-6 mb-8" data-testid="journal-section">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <NotebookPen className="w-5 h-5" style={{ color: clan.color }} />
          <div>
            <div className="label-uppercase" style={{ color: clan.color }}>{t("familyJournal")}</div>
            <p className="text-xs text-[#6E5B4F] mt-0.5">{t("familyJournalDesc")}</p>
          </div>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="border-[#993D22] text-[#993D22]" data-testid="add-journal-btn">
                <Plus className="w-4 h-4 mr-1" /> {t("addJournalEntry")}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#FAF7F2] max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif-heritage text-2xl text-[#261911]">{t("addJournalEntry")}</DialogTitle>
                <DialogDescription className="text-sm text-[#6E5B4F]">{t("familyJournalDesc")}</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="journal-form">
                <div>
                  <Label>{t("journalMonth")}</Label>
                  <Input type="month" value={form.month} onChange={(e) => setForm(p => ({ ...p, month: e.target.value }))} required data-testid="journal-month-input" />
                </div>
                <div>
                  <Label>{t("journalContent")} *</Label>
                  <Textarea rows={5} value={form.content} onChange={(e) => setForm(p => ({ ...p, content: e.target.value }))} required data-testid="journal-content-input" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
                  <Button type="submit" className="btn-terra" disabled={saving} data-testid="submit-journal-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {t("save")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {entries.length === 0 ? (
        <p className="italic text-[#6E5B4F] text-sm" data-testid="journal-empty">{t("noJournal")}</p>
      ) : (
        <ol className="space-y-4" data-testid="journal-list">
          {entries.map((e) => {
            const canDelete = canEdit && (user?.role === "elder" || user?.role === "admin" || e.author_id === user?.id);
            return (
              <li key={e.id} className="pl-4 border-l-2" style={{ borderColor: clan.color }} data-testid={`journal-${e.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="label-uppercase flex items-center gap-1" style={{ color: clan.color }}>
                      <CalendarDays className="w-3 h-3" /> {monthLabel(e.month)}
                    </div>
                    <p className="text-[#261911] whitespace-pre-wrap mt-1">{e.content}</p>
                    <div className="text-xs text-[#6E5B4F] mt-1">— {e.author_name || "anonyme"}</div>
                  </div>
                  {canDelete && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-red-700 hover:text-red-800" data-testid={`journal-delete-${e.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("confirmDelete")}</AlertDialogTitle>
                          <AlertDialogDescription>{monthLabel(e.month)}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(e.id)} className="bg-red-700 hover:bg-red-800">{t("delete")}</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};

export default JournalSection;
